import React, { useState, useEffect, useRef } from 'react';
import { get, post, put } from '../../utils/apiClient';
import { clearCacheKey } from '../../utils/cacheUtils';
import Base64Image from '../Base64Image';
import {
  AdminSectionTitle,
  AdminSectionText,
  AdminLoginLabel,
  AdminLoginInput,
  AdminLoginButton,
  AdminButton,
  AdminSaveButton,
} from '../../styles/AdminPageStyles';

const AdminGalleryPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const [newImage, setNewImage] = useState({
    title: '',
    description: '',
    albumName: '',
    category: '',
    displayOrder: '',
    file: null,
    files: [],
  });
  const galleryFileInputRef = useRef(null);

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await get('/gallery');
        setPageData(data);
      } catch (err) {
        setError(err && err.message ? err.message : 'Failed to load gallery page.');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, []);

  const handleHeaderTitleChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      header: {
        ...((prev && prev.header) || {}),
        title: value,
      },
    }));
  };

  const handleDescriptionChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      description: value,
    }));
  };

  const updateImageField = (index, field, value) => {
    setPageData((prev) => {
      if (!prev || !Array.isArray(prev.images)) return prev;
      const list = prev.images.map((item, i) => (
        i === index
          ? {
              ...item,
              [field]: field === 'displayOrder' && value !== ''
                ? Number(value)
                : value === ''
                  ? undefined
                  : value,
            }
          : item
      ));
      return {
        ...prev,
        images: list,
      };
    });
  };

  const removeImageEntry = async (index) => {
    if (!pageData || !Array.isArray(pageData.images)) return;
    const ok = window.confirm('Remove this gallery image?');
    if (!ok) return;

    setSaving(true);
    setError(null);

    try {
      const nextImages = pageData.images.filter((_, i) => i !== index);

      const payload = {
        header: {
          ...(pageData.header || {}),
          title: (pageData.header && pageData.header.title) || 'Gallery - Church History',
        },
        description: pageData.description || '',
        images: nextImages,
      };

      const updated = await put('/gallery', payload);
      setPageData(updated);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to update gallery images.');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePage = async (e) => {
    e.preventDefault();
    if (!pageData) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const payload = {
        header: {
          ...(pageData.header || {}),
          title: (pageData.header && pageData.header.title) || 'Gallery - Church History',
        },
        description: pageData.description || '',
        images: Array.isArray(pageData.images) ? pageData.images : [],
      };

      const updated = await put('/gallery', payload);
      setPageData(updated);
      setSaveSuccess('Gallery page saved successfully.');
      clearCacheKey('gallery_data');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to save gallery page.');
    } finally {
      setSaving(false);
    }
  };

  const handleNewImageFieldChange = (field, value) => {
    setNewImage((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNewImageFileChange = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setNewImage((prev) => ({
      ...prev,
      file: files[0] || null,
      files,
    }));
  };

  const handleUploadNewImage = async (e) => {
    e.preventDefault();
    const filesToUpload =
      newImage.files && newImage.files.length > 0
        ? newImage.files
        : newImage.file
        ? [newImage.file]
        : [];

    if (filesToUpload.length === 0) {
      setUploadError('Please select at least one image file to upload.');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append('image', file);
        if (newImage.title) formData.append('title', newImage.title);
        if (newImage.description) formData.append('description', newImage.description);
        if (newImage.albumName) formData.append('albumName', newImage.albumName);
        if (newImage.category) formData.append('category', newImage.category);
        if (newImage.displayOrder !== '') formData.append('displayOrder', newImage.displayOrder);

        await post('/gallery/images/upload', formData);
      }
      const data = await get('/gallery');
      setPageData(data);
      setNewImage({
        title: '',
        description: '',
        albumName: '',
        category: '',
        displayOrder: '',
        file: null,
        files: [],
      });
      if (galleryFileInputRef.current) {
        galleryFileInputRef.current.value = '';
      }
      clearCacheKey('gallery_data');
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload gallery image.');
    } finally {
      setUploading(false);
    }
  };

  if (loading && !pageData) {
    return (
      <>
        <AdminSectionTitle>Gallery CMS</AdminSectionTitle>
        <AdminSectionText>Loading gallery page...</AdminSectionText>
      </>
    );
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const description = pageData ? pageData.description : '';
  const images = pageData && Array.isArray(pageData.images) ? pageData.images : [];

  return (
    <>
      <AdminSectionTitle>Gallery CMS</AdminSectionTitle>

      <form onSubmit={handleSavePage} style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="gallery-header-title">Header Title</AdminLoginLabel>
          <AdminLoginInput
            id="gallery-header-title"
            value={(header && header.title) || ''}
            onChange={handleHeaderTitleChange}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="gallery-description">Page Description</AdminLoginLabel>
          <textarea
            id="gallery-description"
            rows={3}
            value={description}
            onChange={handleDescriptionChange}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

      <hr style={{ margin: '16px 0' }} />

      <AdminSectionTitle>Existing Gallery Images</AdminSectionTitle>
      {images.length === 0 && (
        <AdminSectionText style={{ fontSize: '0.9rem' }}>
          No gallery images have been uploaded yet.
        </AdminSectionText>
      )}

      {images.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {images.map((item, index) => (
            <div
              key={`${item.title || 'image'}-${index}`}
              style={{
                border: '1px solid #ddd',
                borderRadius: 8,
                padding: 10,
                marginBottom: 8,
                fontSize: '0.9rem',
              }}
            >
              {item.image && (
                <div style={{ marginBottom: 8 }}>
                  <Base64Image
                    image={item.image}
                    alt={item.title || 'Gallery image'}
                    style={{ width: 200, borderRadius: 6 }}
                  />
                </div>
              )}
              <div style={{ marginBottom: 6 }}>
                <AdminLoginLabel htmlFor={`gallery-title-${index}`}>
                  Title
                </AdminLoginLabel>
                <AdminLoginInput
                  id={`gallery-title-${index}`}
                  value={item.title || ''}
                  onChange={(e) => updateImageField(index, 'title', e.target.value)}
                />
              </div>
              <div style={{ marginBottom: 6 }}>
                <AdminLoginLabel htmlFor={`gallery-album-${index}`}>
                  Album Name (optional)
                </AdminLoginLabel>
                <AdminLoginInput
                  id={`gallery-album-${index}`}
                  value={item.albumName || ''}
                  onChange={(e) => updateImageField(index, 'albumName', e.target.value)}
                />
              </div>
              <div style={{ marginBottom: 6 }}>
                <AdminLoginLabel htmlFor={`gallery-category-${index}`}>
                  Category (optional)
                </AdminLoginLabel>
                <AdminLoginInput
                  id={`gallery-category-${index}`}
                  value={item.category || ''}
                  onChange={(e) => updateImageField(index, 'category', e.target.value)}
                />
              </div>
              <div style={{ marginBottom: 6 }}>
                <AdminLoginLabel htmlFor={`gallery-order-${index}`}>
                  Display Order (optional)
                </AdminLoginLabel>
                <AdminLoginInput
                  id={`gallery-order-${index}`}
                  type="number"
                  value={
                    typeof item.displayOrder === 'number'
                      ? item.displayOrder
                      : ''
                  }
                  onChange={(e) =>
                    updateImageField(index, 'displayOrder', e.target.value)
                  }
                />
              </div>
              <div style={{ marginBottom: 6 }}>
                <AdminLoginLabel htmlFor={`gallery-description-${index}`}>
                  Description (optional)
                </AdminLoginLabel>
                <textarea
                  id={`gallery-description-${index}`}
                  rows={2}
                  value={item.description || ''}
                  onChange={(e) => updateImageField(index, 'description', e.target.value)}
                  style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
                />
              </div>
              <AdminButton type="button" onClick={() => removeImageEntry(index)}>
                Remove Image
              </AdminButton>
            </div>
          ))}
        </div>
      )}

      <hr style={{ margin: '16px 0' }} />

      <AdminSectionTitle>Upload Gallery Image</AdminSectionTitle>
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="new-gallery-title">Title (optional)</AdminLoginLabel>
          <AdminLoginInput
            id="new-gallery-title"
            value={newImage.title}
            onChange={(e) => handleNewImageFieldChange('title', e.target.value)}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="new-gallery-description">Description (optional)</AdminLoginLabel>
          <textarea
            id="new-gallery-description"
            rows={2}
            value={newImage.description}
            onChange={(e) => handleNewImageFieldChange('description', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="new-gallery-album">Album Name (optional)</AdminLoginLabel>
          <AdminLoginInput
            id="new-gallery-album"
            value={newImage.albumName}
            onChange={(e) => handleNewImageFieldChange('albumName', e.target.value)}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="new-gallery-category">Category (optional)</AdminLoginLabel>
          <AdminLoginInput
            id="new-gallery-category"
            value={newImage.category}
            onChange={(e) => handleNewImageFieldChange('category', e.target.value)}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="new-gallery-order">Display Order (optional)</AdminLoginLabel>
          <AdminLoginInput
            id="new-gallery-order"
            type="number"
            value={newImage.displayOrder}
            onChange={(e) => handleNewImageFieldChange('displayOrder', e.target.value)}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="new-gallery-file">Image File(s)</AdminLoginLabel>
          <input
            id="new-gallery-file"
            type="file"
            accept="image/*"
            multiple
            onChange={handleNewImageFileChange}
            ref={galleryFileInputRef}
            style={{ fontSize: '0.9rem' }}
          />
        </div>
        <AdminLoginButton type="button" onClick={handleUploadNewImage} disabled={uploading}>
          {uploading ? 'Uploading...' : 'Upload Gallery Image'}
        </AdminLoginButton>
      </div>

      <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: '16px' }}>
        <AdminSaveButton type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save Gallery Page'}
        </AdminSaveButton>
        {saveSuccess && (
          <span style={{ color: '#1b5e20', fontWeight: '500' }}>{saveSuccess}</span>
        )}
        {error && (
          <span style={{ color: '#b71c1c', fontWeight: '500' }}>{error}</span>
        )}
        {uploadError && (
          <span style={{ color: '#b71c1c', fontWeight: '500' }}>{uploadError}</span>
        )}
      </div>
    </form>
    </>
  );
};

export default AdminGalleryPage;
